import {
  DiagnosticEventType,
  DiagnosticProfile,
} from '../models/diagnostic-profile';

export const DIAGNOSTIC_DEEP_DEBUG_ROLE = 'DIAGNOSTIC_DEEP_DEBUG';

export const ALL_EVENT_TYPES: readonly DiagnosticEventType[] = Object.freeze([
  DiagnosticEventType.ROUTE,
  DiagnosticEventType.API,
  DiagnosticEventType.ERROR,
  DiagnosticEventType.USER_ACTION,
]);

// maxEvents and apiPayload caps are deliberately conservative (EAS-2043) to
// keep the worst-case buffer footprint within email-attachable bundle bounds
// on low-RAM end-user devices. Runtime overrides (EAS-2047/2048) are
// documented in DiagnosticDeepDebugConfigService.
export const DIAGNOSTIC_PROFILES: readonly DiagnosticProfile[] = Object.freeze([
  {
    name: 'default',
    maxEvents: 100,
    maxWindowMs: 5 * 60_000,
    eventTypes: ALL_EVENT_TYPES,
    payloadDepth: 'minimal',
  },
  {
    name: 'debug',
    maxEvents: 200,
    maxWindowMs: 15 * 60_000,
    eventTypes: ALL_EVENT_TYPES,
    payloadDepth: 'standard',
    apiPayload: {
      requestBodyMaxChars: 4096,
    },
  },
  {
    name: 'deepDebug',
    maxEvents: 500,
    maxWindowMs: 30 * 60_000,
    eventTypes: ALL_EVENT_TYPES,
    requiredRole: DIAGNOSTIC_DEEP_DEBUG_ROLE,
    payloadDepth: 'verbose',
    apiPayload: {
      requestBodyMaxChars: 8192,
      responseBodyMaxChars: 8192,
      captureHeaders: true,
    },
  },
  // Virtual "disabled" profile. eventTypes: [] makes every push() a no-op
  // in DiagnosticBufferService (filtered by activeProfile.eventTypes). The
  // profile is always available (no requiredRole) and is placed last so that
  // profiles[0] remains 'default' — the fallback when no preference is stored.
  {
    name: 'disabled',
    maxEvents: 0,
    maxWindowMs: 0,
    eventTypes: Object.freeze<DiagnosticEventType[]>([]),
    payloadDepth: 'minimal',
  },
] as const);

/**
 * Bootstrap-time privacy guard: a profile that captures response body
 * excerpts or HTTP headers MUST declare a `requiredRole`. Throwing at
 * module-load surfaces a misconfiguration before any deploy reaches an
 * environment.
 */
export function validateDiagnosticProfiles(
  profiles: readonly DiagnosticProfile[]
): void {
  for (const profile of profiles) {
    const collectsSensitive =
      profile.apiPayload?.responseBodyMaxChars !== undefined ||
      profile.apiPayload?.captureHeaders === true;
    if (collectsSensitive && !profile.requiredRole) {
      throw new Error(
        `[DIAGNOSTIC_PROFILES] responseBody/captureHeaders requires requiredRole on profile: ${profile.name}`
      );
    }
  }
}

validateDiagnosticProfiles(DIAGNOSTIC_PROFILES);
